import React, { useState, useMemo } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { Search, User, Calendar, ArrowRight } from 'lucide-react';
import '../../../assets/styles/customer/PublicBlog.css';
import MyLayout from '../../../layouts/customer/MyLayout';

/* ── Mock Data ── */
const BLOG_POSTS = [
  {
    id: 1,
    title: 'First Blog Post',
    slug: 'first-blog-post',
    excerpt: 'testing description of the blog',
    content: 'testing content of the blog, testing content of the blog, testing content of the blog, testing content of the blog, testing content of the blog',
    author: 'Hamza',
    date: 'April 4, 2026',
    tags: ['dairy', 'farming', 'milk'],
    coverImage: null,
    readTime: '1 min read',
  },
  {
    id: 2,
    title: 'The Future of Milk Distribution',
    slug: 'future-of-milk-distribution',
    excerpt: 'How technology is transforming Pakistan\'s dairy supply chain.',
    content: 'Full content about milk distribution...',
    author: 'Ahmed',
    date: 'March 28, 2026',
    tags: ['dairy', 'technology'],
    coverImage: null,
    readTime: '3 min read',
  },
  {
    id: 3,
    title: 'Benefits of Fresh Milk Daily',
    slug: 'benefits-of-fresh-milk',
    excerpt: 'Why fresh milk delivered daily is better for your family.',
    content: 'Fresh milk benefits content...',
    author: 'Sara',
    date: 'March 20, 2026',
    tags: ['milk', 'health'],
    coverImage: null,
    readTime: '2 min read',
  },
];



/* ── Footer ── */
// const PublicFooter = () => {
//   const navigate = useNavigate();
//   return (
//     <footer className="pblog-footer">
//       <Container>
//         <Row className="pblog-footer__top">
//           <Col xs={12} md={4} className="pblog-footer__brand-col">
//             <div className="pblog-footer__brand">
//               <div className="pblog-footer__logo">🥛</div>
//               <span className="pblog-footer__name">MilkFlow</span>
//             </div>
//             <p className="pblog-footer__tagline">
//               Simplifying dairy delivery management for Pakistan's local milk supply chain.
//             </p>
//           </Col>
//           <Col xs={6} md={2}>
//             <p className="pblog-footer__col-title">Product</p>
//             <ul className="pblog-footer__list">
//               <li onClick={() => navigate('/pricing')}>Pricing</li>
//               <li onClick={() => navigate('/about')}>About</li>
//               <li onClick={() => navigate('/contact')}>Contact</li>
//             </ul>
//           </Col>
//           <Col xs={6} md={3}>
//             <p className="pblog-footer__col-title">For Users</p>
//             <ul className="pblog-footer__list">
//               <li onClick={() => navigate('/signup')}>Customer Signup</li>
//               <li onClick={() => navigate('/login')}>Login</li>
//             </ul>
//           </Col>
//           <Col xs={6} md={3}>
//             <p className="pblog-footer__col-title">For Distributors</p>
//             <ul className="pblog-footer__list">
//               <li onClick={() => navigate('/become-distributor')}>Become a Distributor</li>
//               <li onClick={() => navigate('/distributor-login')}>Distributor Login</li>
//             </ul>
//           </Col>
//         </Row>
//         <div className="pblog-footer__bottom">
//           <p>© 2024 MilkFlow. All rights reserved.</p>
//         </div>
//       </Container>
//     </footer>
//   );
// };

/* ── Blog Card ── */
const BlogCard = ({ post, onClick }) => (
  <div className="pblog-card" onClick={() => onClick(post.slug)}>
    <div className="pblog-card__cover">
      {post.coverImage
        ? <img src={post.coverImage} alt={post.title} className="pblog-card__img" />
        : <div className="pblog-card__cover-placeholder">
            <span className="pblog-card__cover-icon">🥛</span>
          </div>
      }
      <div className="pblog-card__tags">
        {post.tags.map(t => (
          <span key={t} className="pblog-tag">{t}</span>
        ))}
      </div>
    </div>
    <div className="pblog-card__body">
      <h3 className="pblog-card__title">{post.title}</h3>
      <p className="pblog-card__excerpt">{post.excerpt}</p>
      <div className="pblog-card__meta">
        <span className="pblog-card__author">
          <User size={13} /> {post.author}
        </span>
        <span className="pblog-card__date">
          <Calendar size={13} /> {post.date}
        </span>
      </div>
      <button className="pblog-card__read-more">
        Read More <ArrowRight size={14} />
      </button>
    </div>
  </div>
);

/* ══════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════ */
const PublicBlog = () => {
  const navigate   = useNavigate();
  const [query,    setQuery]    = useState('');
  const [activeTag, setActiveTag] = useState('');

  /* Collect all unique tags */
  const allTags = useMemo(() => {
    const set = new Set();
    BLOG_POSTS.forEach(p => p.tags.forEach(t => set.add(t)));
    return [...set];
  }, []);

  /* Filter */
  const filtered = useMemo(() => {
    return BLOG_POSTS.filter(p => {
      const matchQ = !query ||
        p.title.toLowerCase().includes(query.toLowerCase()) ||
        p.excerpt.toLowerCase().includes(query.toLowerCase()) ||
        p.tags.some(t => t.toLowerCase().includes(query.toLowerCase()));
      const matchT = !activeTag || p.tags.includes(activeTag);
      return matchQ && matchT;
    });
  }, [query, activeTag]);

  return (
    <MyLayout>
    <div className="pblog-root">

      {/* ── Hero ── */}
      <section className="pblog-hero">
        <div className="pblog-hero__bg" />
        <Container>
          <div className="pblog-hero__content">
            <h1 className="pblog-hero__title">
              Our <span className="pblog-hero__accent">Blog</span>
            </h1>
            <p className="pblog-hero__sub">
              Insights, tips, and stories from the dairy industry. Stay updated with the
              latest trends in milk distribution and farming.
            </p>

            {/* Search */}
            <div className="pblog-search">
              <Search size={16} className="pblog-search__icon" />
              <input
                className="pblog-search__input"
                type="text"
                placeholder="Search by title, keyword, or tag..."
                value={query}
                onChange={e => setQuery(e.target.value)}
              />
            </div>
          </div>
        </Container>
      </section>

      {/* ── Tag Filters ── */}
      <section className="pblog-tags-bar">
        <Container>
          <div className="pblog-tags-bar__inner">
            <button
              className={`pblog-tag-btn ${!activeTag ? 'pblog-tag-btn--active' : ''}`}
              onClick={() => setActiveTag('')}
            >
              All
            </button>
            {allTags.map(t => (
              <button
                key={t}
                className={`pblog-tag-btn ${activeTag === t ? 'pblog-tag-btn--active' : ''}`}
                onClick={() => setActiveTag(activeTag === t ? '' : t)}
              >
                {t}
              </button>
            ))}
          </div>
        </Container>
      </section>

      {/* ── Posts Grid ── */}
      <section className="pblog-grid-section">
        <Container>
          {filtered.length === 0 ? (
            <div className="pblog-empty">
              <p className="pblog-empty__msg">No posts found. Try a different search or tag.</p>
            </div>
          ) : (
            <Row className="g-4">
              {filtered.map((post, i) => (
                <Col xs={12} sm={6} lg={4} key={post.id} style={{ animationDelay: `${i * 0.1}s` }}>
                  <BlogCard post={post} onClick={slug => navigate(`/blog/Publicblogdetail`)} />
                </Col>
              ))}
            </Row>
          )}
        </Container>
      </section>

    </div>
    </MyLayout>
  );
};

export default PublicBlog;